/**
 * Configurador del servilletero BUBBLE.
 *
 * Producto paramétrico: no hay lienzo 2D, así que no usa el Customizer. La vista
 * previa se pide a `parametric-preview/`, que no guarda nada; «Guardar» crea o
 * actualiza el diseño y genera en segundo plano sus ficheros (miniatura, GLB,
 * 3MF) para «Mis diseños» y los pedidos.
 *
 * Los parámetros vienen del schema que genera el backend (`ParamPanel`); la
 * semilla y el color tienen control propio.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import {
    AlertTriangle, ArrowLeft, CheckCircle2, Dices, Download, Eye, EyeOff, Loader2, RotateCcw, Save, Send, Undo2,
} from 'lucide-react';
import ParamPanel from '../Customiz3r/components/ToolSelectorRight/components/ParamPanel/ParamPanel.jsx';
import CustomOrderRequestModal from '../Customiz3r/components/CustomOrderRequestModal.jsx';
import { useAuthContext } from '../../components/providers/AuthProvider.jsx';
import { canDownloadDesignFiles } from '../../lib/permissions.js';
import { EditableTitle, SaveStatus, showErrorAlert } from '../../components/ui';
import { showConfirm } from '../../components/HelperAlerts/HelpersAlerts.jsx';
import {
    buildDefaultDesignName, getProduct, patchPath, readPath, withProductDefaults,
} from '../../products/registry.js';
import BubbleViewer from './BubbleViewer.jsx';
import {
    currentSeed, geometryKey, hiddenFieldsFor, seedHistory, withNewSeed, withPreviousSeed,
} from './bubbleState.js';

const PRODUCT = getProduct('napkin_bubble');
const PREVIEW_DEBOUNCE_MS = 450;
const COLOR_FIELD = PRODUCT.paramSchema.groups
    .flatMap((group) => group.fields)
    .find((field) => field.key === 'body_color');

const VIEWS = [
    ['iso', '3/4'], ['front', 'Frontal'], ['back', 'Trasera'],
    ['left', 'Lateral'], ['top', 'Superior'],
];

const base64ToArrayBuffer = (text) => {
    const binary = window.atob(text);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
    return bytes.buffer;
};

const formatMm = (value) => (value == null ? '—' : `${Math.round(Number(value))} mm`);

function BubbleConfigurator() {
    const { modelId } = useParams();
    const navigate = useNavigate();
    const authContext = useAuthContext();
    const canDownload = canDownloadDesignFiles(authContext);

    const [values, setValues] = useState(() => withProductDefaults({ ...PRODUCT.defaults }));
    const [name, setName] = useState(() => buildDefaultDesignName(PRODUCT));
    const [loadingModel, setLoadingModel] = useState(Boolean(modelId));
    const [saved, setSaved] = useState(null);           // { values, name } tal cual se guardó
    const [saveStatus, setSaveStatus] = useState('dirty');
    const [lastSavedAt, setLastSavedAt] = useState(null);

    const [glb, setGlb] = useState(null);
    const [preview, setPreview] = useState({ phase: 'idle', metrics: {}, errors: [], warnings: [] });
    const requestRef = useRef(0);

    const [showNapkins, setShowNapkins] = useState(true);
    const [showSlot, setShowSlot] = useState(false);
    const [view, setView] = useState({ name: null, nonce: 0 });
    const [orderKeyring, setOrderKeyring] = useState(null);
    const [exporting, setExporting] = useState(false);

    // ---------------------------------------------------------------- carga
    useEffect(() => {
        if (!modelId) return;
        let cancelled = false;
        setLoadingModel(true);
        axios.get(`customized-keyrings/${modelId}/`)
            .then(({ data }) => {
                if (cancelled) return;
                const loaded = withProductDefaults(data.form2d_data || {});
                if (loaded.productType !== 'napkin_bubble') {
                    navigate(`${getProduct(loaded.productType).routeBase}/${modelId}`, { replace: true });
                    return;
                }
                setValues(loaded);
                setName(data.name);
                setSaved({ values: loaded, name: data.name });
                setSaveStatus('saved');
                setLastSavedAt(data.modified_at ? new Date(data.modified_at) : null);
            })
            .catch(() => showErrorAlert({ text: 'No se pudo cargar el diseño.' }))
            .finally(() => { if (!cancelled) setLoadingModel(false); });
        return () => { cancelled = true; };
    }, [modelId, navigate]);

    useEffect(() => {
        if (!saved) return;
        const clean = JSON.stringify(saved.values) === JSON.stringify(values) && saved.name === name;
        setSaveStatus((prev) => (prev === 'saving' ? prev : clean ? 'saved' : 'dirty'));
    }, [values, name, saved]);

    // --------------------------------------------------------- vista previa
    const geometry = geometryKey(values);
    useEffect(() => {
        if (loadingModel) return undefined;
        const request = requestRef.current + 1;
        requestRef.current = request;
        setPreview((prev) => ({ ...prev, phase: 'generating' }));
        const timer = setTimeout(() => {
            axios.post('parametric-preview/', { form2d_data: values })
                .then(({ data }) => {
                    // Una respuesta vieja que llega tarde no pisa a la nueva.
                    if (request !== requestRef.current) return;
                    if (data.glb_base64) setGlb(base64ToArrayBuffer(data.glb_base64));
                    setPreview({
                        phase: data.ok ? 'ready' : 'invalid',
                        metrics: data.metrics || {},
                        errors: data.errors || [],
                        warnings: data.warnings || [],
                    });
                })
                .catch(() => {
                    if (request !== requestRef.current) return;
                    setPreview((prev) => ({ ...prev, phase: 'error' }));
                });
        }, PREVIEW_DEBOUNCE_MS);
        return () => clearTimeout(timer);
        // Solo la geometría: el color y el historial de semillas no regeneran.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [geometry, loadingModel]);

    // -------------------------------------------------------------- acciones
    const onChange = useCallback((path, value) => setValues((prev) => patchPath(prev, path, value)), []);

    const persist = useCallback(async () => {
        setSaveStatus('saving');
        const payload = { name, form2d_data: values };
        try {
            let id = modelId;
            if (id) {
                await axios.put(`customized-keyrings/${id}/`, payload);
            } else {
                const { data } = await axios.post('customized-keyrings/', payload);
                id = data.id;
            }
            setSaved({ values, name });
            setSaveStatus('saved');
            setLastSavedAt(new Date());
            // Miniatura, GLB y 3MF del diseño guardado, sin esperar por ellos.
            axios.post(`customized-keyrings/${id}/regenerate-3d/`, { form2d_data: values })
                .catch((error) => console.warn('No se pudieron generar los ficheros del diseño:', error));
            if (!modelId) navigate(`${PRODUCT.routeBase}/${id}`, { replace: true });
            return id;
        } catch (error) {
            setSaveStatus('error');
            showErrorAlert({ text: 'No se pudo guardar el diseño.' });
            throw error;
        }
    }, [name, values, modelId, navigate]);

    const ensureSaved = useCallback(
        async () => (saveStatus === 'saved' && modelId ? modelId : persist()),
        [saveStatus, modelId, persist],
    );

    const reset = () => setValues(withProductDefaults({ ...PRODUCT.defaults }));

    const requestOrder = async () => {
        try {
            const id = await ensureSaved();
            setOrderKeyring({ id: Number(id), name, svg_2d: '' });
        } catch { /* ya avisado */ }
    };

    const exportStl = async () => {
        setExporting(true);
        try {
            const id = await ensureSaved();
            const response = await axios.get(`customized-keyrings/${id}/export-stl/`, { responseType: 'blob' });
            const disposition = response.headers['content-disposition'] || '';
            const fileName = /filename="([^"]+)"/.exec(disposition)?.[1] || `servilletero_${id}.zip`;
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            let detail = 'No se pudo exportar el STL.';
            try {
                const body = JSON.parse(await error?.response?.data?.text?.());
                if (body?.errors?.length) detail = body.errors.join(' ');
            } catch { /* respuesta no JSON */ }
            showErrorAlert({ text: detail });
        } finally {
            setExporting(false);
        }
    };

    // ------------------------------------------------------------------ vista
    const product = useMemo(
        () => ({ ...PRODUCT, hiddenSchemaFields: hiddenFieldsFor(values, PRODUCT.hiddenSchemaFields) }),
        [values],
    );
    const bodyColor = readPath(values, 'bubble.bodyColor') || COLOR_FIELD.default;
    const metrics = preview.metrics || {};
    const size = metrics.size_mm || {};
    const generating = preview.phase === 'generating';
    const printWarnings = metrics.print_warnings || [];
    const otherWarnings = (preview.warnings || []).filter((warning) => !printWarnings.includes(warning));

    const seedControls = (
        <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between text-xs">
                <span className="font-semibold text-base-content/80">Semilla</span>
                <code className="rounded bg-base-300 px-2 py-0.5">{currentSeed(values)}</code>
            </div>
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => setValues((prev) => withNewSeed(prev))}>
                <Dices className="h-4 w-4" /> Generar nuevas pompas
            </button>
            <button
                type="button"
                className="btn btn-ghost btn-sm"
                disabled={!seedHistory(values).length}
                onClick={() => setValues((prev) => withPreviousSeed(prev))}
            >
                <Undo2 className="h-4 w-4" /> Distribución anterior
            </button>
            <p className="text-[11px] leading-snug text-base-content/60">
                Cambia solo la colocación de las pompas: medidas, hueco, densidad y tamaño se mantienen.
            </p>
        </div>
    );

    const colorControls = (
        <div className="grid grid-cols-3 gap-2">
            {COLOR_FIELD.options.map((option) => {
                const selected = option.value === bodyColor;
                return (
                    <button
                        key={option.value}
                        type="button"
                        onClick={() => onChange(COLOR_FIELD.path, option.value)}
                        aria-pressed={selected}
                        className={`flex flex-col items-center gap-1 rounded-lg border p-2 text-[11px] transition ${
                            selected ? 'border-primary bg-primary/10' : 'border-base-300 hover:border-base-content/30'
                        }`}
                    >
                        <span className="h-7 w-7 rounded-full border border-base-content/20" style={{ background: option.value }} />
                        {option.label}
                    </button>
                );
            })}
        </div>
    );

    const validation = (
        <div className="mt-1 flex flex-col gap-2 rounded-xl bg-base-200/60 p-3 text-xs">
            <span className="text-sm font-semibold">Comprobación de impresión</span>
            {preview.phase === 'invalid' && preview.errors.map((error) => (
                <div key={error} className="flex gap-2 text-error"><AlertTriangle className="h-4 w-4 shrink-0" />{error}</div>
            ))}
            {preview.phase === 'error' && (
                <div className="flex gap-2 text-error"><AlertTriangle className="h-4 w-4 shrink-0" />No se pudo generar la vista previa.</div>
            )}
            {preview.phase === 'ready' && !printWarnings.length && (
                <div className="flex gap-2 text-success">
                    <CheckCircle2 className="h-4 w-4 shrink-0" />Una sola pieza, hueco libre y base plana.
                </div>
            )}
            {[...printWarnings, ...otherWarnings].map((warning) => (
                <div key={warning} className="flex gap-2 text-warning"><AlertTriangle className="h-4 w-4 shrink-0" />{warning}</div>
            ))}
            {metrics.bubble_count != null && (
                <span className="text-base-content/60">
                    {metrics.bubble_count} pompas · voladizo {Math.round(metrics.overhang_mm2 ?? 0)} mm² · lámina {metrics.core_wall_mm} mm
                </span>
            )}
        </div>
    );

    const actions = (
        <div className="mt-2 flex flex-col gap-2">
            <div className="grid grid-cols-2 gap-2">
                <button type="button" className="btn btn-primary btn-sm" onClick={() => persist().catch(() => {})}
                        disabled={saveStatus === 'saving'}>
                    <Save className="h-4 w-4" /> Guardar
                </button>
                <button type="button" className="btn btn-ghost btn-sm" onClick={reset}>
                    <RotateCcw className="h-4 w-4" /> Restablecer
                </button>
            </div>
            <button type="button" className="btn btn-outline btn-sm" onClick={requestOrder}
                    disabled={preview.phase === 'invalid'}>
                <Send className="h-4 w-4" /> Pedir este diseño
            </button>
            {canDownload && (
                <button type="button" className="btn btn-outline btn-sm" onClick={exportStl}
                        disabled={exporting || preview.phase === 'invalid'}>
                    {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                    Exportar STL + parámetros
                </button>
            )}
        </div>
    );

    return (
        // Pantalla completa: la barra de navegación se oculta en las rutas de
        // producto del registry (HomeNavBar), así que la salida va en el panel.
        <div className="flex h-screen w-full flex-col overflow-y-auto bg-base-100 lg:flex-row lg:overflow-hidden">
            <section className="relative min-h-[45vh] flex-1 bg-gradient-to-b from-base-200 to-base-100">
                <BubbleViewer
                    className="absolute inset-0"
                    glb={glb}
                    bodyColor={bodyColor}
                    showNapkins={showNapkins}
                    showSlot={showSlot}
                    view={view.name}
                    viewNonce={view.nonce}
                />

                <div className="absolute left-3 top-3 flex flex-wrap gap-1">
                    {VIEWS.map(([key, label]) => (
                        <button key={key} type="button" className="btn btn-xs bg-base-100/80 backdrop-blur"
                                onClick={() => setView((prev) => ({ name: key, nonce: prev.nonce + 1 }))}>
                            {label}
                        </button>
                    ))}
                </div>
                <div className="absolute right-3 top-3 flex flex-col items-end gap-1">
                    <button type="button" className="btn btn-xs bg-base-100/80 backdrop-blur" onClick={() => setShowNapkins((v) => !v)}>
                        {showNapkins ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                        {showNapkins ? 'Ocultar servilletas' : 'Mostrar servilletas'}
                    </button>
                    <button type="button" className="btn btn-xs bg-base-100/80 backdrop-blur" onClick={() => setShowSlot((v) => !v)}>
                        {showSlot ? 'Ocultar hueco' : 'Ver hueco interior'}
                    </button>
                </div>

                {(generating || loadingModel || !glb) && (
                    <div className="absolute inset-x-0 top-14 flex justify-center">
                        <span className="flex items-center gap-2 rounded-full bg-base-100/90 px-3 py-1 text-xs shadow">
                            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Generando pompas…
                        </span>
                    </div>
                )}

                <dl className="absolute bottom-3 left-3 grid grid-cols-3 gap-x-4 gap-y-1 rounded-xl bg-base-100/85 px-3 py-2 text-xs shadow backdrop-blur">
                    <dt className="text-base-content/60">Ancho</dt>
                    <dt className="text-base-content/60">Fondo</dt>
                    <dt className="text-base-content/60">Alto</dt>
                    <dd className="font-semibold">{formatMm(size.width)}</dd>
                    <dd className="font-semibold">{formatMm(size.depth)}</dd>
                    <dd className="font-semibold">{formatMm(size.height)}</dd>
                    <dt className="col-span-3 mt-1 text-base-content/60">
                        Hueco {metrics.slot_gap_mm ?? '—'} mm · paredes {formatMm(metrics.wall_height_mm)}
                        {metrics.tilt_deg ? ` · ${metrics.tilt_deg}°` : ''}
                    </dt>
                </dl>
            </section>

            <aside className="flex w-full flex-col border-base-300 bg-base-100 lg:w-[380px] lg:border-l">
                <header className="flex items-center justify-between gap-2 border-b border-base-300 px-3 py-2">
                    <button
                        type="button"
                        className="btn btn-ghost btn-sm btn-square"
                        title="Volver a los configuradores"
                        aria-label="Volver a los configuradores"
                        onClick={async () => {
                            if (saveStatus !== 'saved') {
                                const { isConfirmed } = await showConfirm({
                                    title: 'Cambios sin guardar',
                                    text: 'Si sales ahora se perderán los cambios del servilletero.',
                                    icon: 'warning',
                                    showCancelButton: true,
                                    confirmButtonText: 'Salir sin guardar',
                                    cancelButtonText: 'Seguir editando',
                                });
                                if (!isConfirmed) return;
                            }
                            navigate('/customizers');
                        }}
                    >
                        <ArrowLeft className="h-4 w-4" />
                    </button>
                    <EditableTitle value={name} onCommit={(next) => setName(next)} className="min-w-0 flex-1" />
                    <SaveStatus status={saveStatus} lastSavedAt={lastSavedAt} onRetry={() => persist().catch(() => {})} />
                </header>
                <div className="flex-1 overflow-y-auto p-2">
                    <ParamPanel
                        product={product}
                        values={values}
                        onChange={onChange}
                        groupActions={{ distribucion: seedControls, color: colorControls }}
                        footer={<>{validation}{actions}</>}
                    />
                </div>
            </aside>

            {orderKeyring && (
                <CustomOrderRequestModal keyring={orderKeyring} onClose={() => setOrderKeyring(null)} />
            )}
        </div>
    );
}

export default BubbleConfigurator;
